import React, { Fragment, useEffect, useRef, useState } from "react";
import debounce from 'lodash/debounce';
import { Button, FormGroup, Label } from 'reactstrap';
import Select, { components } from "react-select";
import { codeWithColourAutoSuggestion } from "../../../../../services/manage-product/productService";

const AvailableLengthTable = (props) => {
    const { setValue, trigger, settingAvailableLengthTypes, availableLengthRows, setAvailableLengthRows } = props;

    // New: duplicate tracking state for length_type only
    const [duplicateIndices, setDuplicateIndices] = useState([]);
    const [availableInTypeOptions, setAvailableInTypeOptions] = useState([]);
    const [pageAvailableInType, setPageAvailableInType] = useState(1);
    const [hasMoreAvailableInType, setHasMoreAvailableInType] = useState(true);
    const [loadingAvailableInType, setLoadingAvailableInType] = useState(false);
    const [queryAvailableInType, setQueryAvailableInType] = useState('');

    // Fetch functions (copy from general.jsx, but use your API import)
    const fetchAvailableInTypeOptions = async (inputValue, page, currentRowIdx = null) => {
        if (loadingAvailableInType || !hasMoreAvailableInType) return;
        setLoadingAvailableInType(true);
        try {
            // Build exclude_ids from current row's selected lengths, excluding the other row
            const excludeIds = [];
            (availableLengthRows || []).forEach((row, i) => {
                if (i !== currentRowIdx) return;
                (row.lengths || []).forEach(item => {
                    if (!item) return;
                    const id = typeof item === 'object' ? item.id : item;
                    if (id) excludeIds.push(id);
                });
            });

            let searchData = { q: inputValue, page: page, page_size: 10 };
            if (excludeIds.length) searchData.exclude_ids = Array.from(new Set(excludeIds));

            const response = await codeWithColourAutoSuggestion(searchData);
            const newOptions = response.response;
            setAvailableInTypeOptions(prevOptions => (page === 1 ? newOptions : [...prevOptions, ...newOptions]));
            setHasMoreAvailableInType(newOptions.length === 10);
            setPageAvailableInType(page + 1);
            setLoadingAvailableInType(false);
        } catch (error) {
            setLoadingAvailableInType(false);
            console.error('Error fetching availableInType options:', error);
        }
    };

    // Debounced fetch
    const debouncedFetchAvailableInTypeOptions = React.useCallback(debounce((inputValue, page, currentRowIdx) => fetchAvailableInTypeOptions(inputValue, page, currentRowIdx), 500), [availableLengthRows]);

    // Infinite scroll handlers
    const handleMenuScrollToBottomAvailableInType = (idx) => {
        if (hasMoreAvailableInType) {
            fetchAvailableInTypeOptions(queryAvailableInType, pageAvailableInType, idx);
        }
    };

    // Menu open handlers
    const handleMenuOpenAvailableInType = (idx) => {
        fetchAvailableInTypeOptions(queryAvailableInType, 1, idx);
    };

    // Input change handlers
    const handleInputChangeAvailableInType = (inputValue, idx) => {
        setPageAvailableInType(1);
        setHasMoreAvailableInType(true);
        setQueryAvailableInType(inputValue);
        debouncedFetchAvailableInTypeOptions(inputValue, 1, idx);
    };

    const handleAvailableLengthRowChange = (idx, field, value) => {
        // If length type changes, reset lengths for that row so previous selections don't persist
        if (field === 'length_type') {
            setAvailableLengthRows(prev => prev.map((row, i) => i === idx ? { ...row, length_type: value, lengths: [] } : row));
        } else {
            setAvailableLengthRows(prev => prev.map((row, i) => i === idx ? { ...row, [field]: value } : row));
        }
    };

    const handleAddAvailableLengthRow = () => {
        setAvailableLengthRows(prev => ([...prev, { length_type: '', lengths: [] }]));
    };

    const handleRemoveAvailableLengthRow = (idx) => {
        setAvailableLengthRows(prev => prev.length > 0 ? prev.filter((_, i) => i !== idx) : prev);
    };

    useEffect(() => {
        setValue("available_lengths", [...availableLengthRows]);
        trigger("available_lengths");
    }, [availableLengthRows]);

    // New: detect duplicate 'length_type' values and expose a form-level flag
    useEffect(() => {
        const keyMap = {};
        (availableLengthRows || []).forEach((row, idx) => {
            const key = `${row.length_type || ''}`;
            const isEmpty = !row.length_type;
            if (isEmpty) return;
            if (!keyMap[key]) keyMap[key] = [];
            keyMap[key].push(idx);
        });
        const duplicates = new Set();
        Object.values(keyMap).forEach(arr => { if (arr.length > 1) arr.forEach(i => duplicates.add(i)); });
        const dupArray = Array.from(duplicates);
        setDuplicateIndices(dupArray);
        const hasDuplicates = dupArray.length > 0;
        if (setValue) setValue("available_lengths_has_duplicates", hasDuplicates);
        if (trigger) trigger("available_lengths_has_duplicates");
    }, [availableLengthRows, setValue, trigger]);

    return (
        <FormGroup>
            <Label className="form-label ai-form-heading">Also Available in Length (Midi, Mini..)</Label>
            <table className="table table-bordered table-sm align-middle">
                <thead>
                    <tr>
                        <th style={{ width: '30%' }}>Type</th>
                        <th style={{ width: '60%' }}>Items</th>
                        <th style={{ width: '10%' }}>Action</th>
                    </tr>
                </thead>
                <tbody>
                    {(availableLengthRows || []).map((row, idx) => {
                        const isDuplicate = duplicateIndices.includes(idx);

                        const selectErrorStyles = {
                            control: (base, state) => ({
                                ...base,
                                borderColor: isDuplicate ? '#dc3545' : base.borderColor,
                                boxShadow: isDuplicate ? '0 0 0 1px rgba(220,53,69,0.12)' : base.boxShadow,
                                '&:hover': { borderColor: isDuplicate ? '#dc3545' : base.borderColor },
                            })
                        };

                        return (
                            <tr key={idx}>
                                <td>
                                    <Select
                                        placeholder="Select Length Type"
                                        options={settingAvailableLengthTypes || []}
                                        getOptionValue={option => option.id}
                                        getOptionLabel={option => option.name}
                                        value={settingAvailableLengthTypes?.find(opt => opt.id === row.length_type) || null}
                                        onChange={selected => handleAvailableLengthRowChange(idx, 'length_type', selected ? selected.id : '')}
                                        isClearable={true}
                                        styles={selectErrorStyles}
                                    />
                                </td>
                                <td>
                                    <Select
                                        placeholder="Select Length Items"
                                        isMulti
                                        options={availableInTypeOptions}
                                        getOptionValue={option => option.id}
                                        getOptionLabel={option => option.name}
                                        value={row.lengths}
                                        onChange={selected => handleAvailableLengthRowChange(idx, 'lengths', selected || [])}
                                        onInputChange={value => handleInputChangeAvailableInType(value, idx)}
                                        onMenuScrollToBottom={() => handleMenuScrollToBottomAvailableInType(idx)}
                                        onMenuOpen={() => handleMenuOpenAvailableInType(idx)}
                                        isLoading={loadingAvailableInType}
                                        isClearable={true}
                                        isDisabled={!row.length_type} // disable until a length type is selected
                                    />
                                </td>
                                <td className="text-center">
                                    <Button 
                                        color="danger" 
                                        size="sm" 
                                        onClick={() => handleRemoveAvailableLengthRow(idx)}
                                    ><i className="fa fa-trash" /></Button>
                                    {isDuplicate && <div className="text-danger small mt-1">Duplicate row</div>}
                                </td>
                            </tr>
                        );
                    })}
                 </tbody>
             </table>
             <Button color="success" size="sm" className="mt-2" onClick={handleAddAvailableLengthRow}><i className="fa fa-plus" /> Add Row</Button>
         </FormGroup>
     );
 };

 export default AvailableLengthTable;
